import { createSlice } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import {
	TaskAndSubtaskCommentInfo,
	TaskAndSubtaskHistoryInfo,
} from "../../pages/TasksAndSubTasks/schema/tasks-and-subtasks-activity";

import { taskAndSubtaskActivityApi } from "./task-and-subtasks-activity.api";

interface TaskAndSubtaskActivityState {
	comments: TaskAndSubtaskCommentInfo[];
	history: TaskAndSubtaskHistoryInfo[];
}

const initialState: TaskAndSubtaskActivityState = {
	comments: [],
	history: [],
};

export const taskAndSubtaskActivitySlice = createSlice({
	name: "tasks-and-subtasks-activity",
	initialState,
	reducers: {
		addComment: (state, { payload }) => {
			const existing = state.comments.some((c) => c.id === payload.id);

			if (!existing) {
				state.comments.unshift(payload);
			}
		},
		updateComment: (state, { payload }) => {
			const index = state.comments.findIndex((c) => c.id === payload.id);

			if (index !== -1) {
				state.comments[index] = payload;
			}
		},
		removeComment: (state, { payload }) => {
			state.comments = state.comments.filter((c) => c.id !== payload);
		},
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(
				taskAndSubtaskActivityApi.endpoints.getCommentsByTask.matchFulfilled,
				(state, { payload }) => {
					const incoming = payload ?? [];
					const existingIds = new Set(incoming.map((c) => c.id));
					const filtered = state.comments.filter((c) => !existingIds.has(c.id));

					state.comments = [...filtered, ...incoming];
				}
			)
			.addMatcher(
				taskAndSubtaskActivityApi.endpoints.getCommentsAll.matchFulfilled,
				(state, action) => {
					state.comments = action.payload;
				}
			)
			.addMatcher(
				taskAndSubtaskActivityApi.endpoints.getCommentsBySubtask.matchFulfilled,
				(state, { payload }) => {
					const incoming = payload ?? [];
					const existingIds = new Set(incoming.map((c) => c.id));
					const filtered = state.comments.filter((c) => !existingIds.has(c.id));

					state.comments = [...filtered, ...incoming];
				}
			)
			.addMatcher(
				taskAndSubtaskActivityApi.endpoints.getHistoryByTask.matchFulfilled,
				(state, { payload }) => {
					state.history = payload ?? [];
				}
			);

		builder.addMatcher(
			taskAndSubtaskActivityApi.endpoints.createComment.matchFulfilled,
			(state, { payload }) => {
				if (payload?.id) {
					state.comments = [payload, ...state.comments];
				}
			}
		);

		builder.addMatcher(
			taskAndSubtaskActivityApi.endpoints.getCommentsAll.matchFulfilled,
			(state, action) => {
				state.comments = action.payload;
			}
		);

		builder
			.addMatcher(
				taskAndSubtaskActivityApi.endpoints.updateComment.matchFulfilled,
				(state, { payload }) => {
					if (payload?.id) {
						const index = state.comments.findIndex((comment) => comment.id === payload.id);

						if (index !== -1) {
							state.comments[index] = payload;
						}
					}
				}
			)

			.addMatcher(
				taskAndSubtaskActivityApi.endpoints.deleteComment.matchFulfilled,
				(state, { meta }) => {
					const id = meta.arg.originalArgs;

					state.comments = state.comments.filter((comment) => comment.id !== id);
				}
			)

			.addMatcher(
				taskAndSubtaskActivityApi.endpoints.getHistoryBySubtask.matchFulfilled,
				(state, { payload }) => {
					state.history = payload ?? [];
				}
			);
	},
});

export const tasksAndSubtasksActivityReducer = taskAndSubtaskActivitySlice.reducer;
export const commentsSelector = (state: RootState) =>
	state[taskAndSubtaskActivitySlice.name].comments;
export const historySelector = (state: RootState) =>
	state[taskAndSubtaskActivitySlice.name].history;
